import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, BarChart3 } from 'lucide-react';
import { SiteHeader, SiteFooter } from './Layout';
import TrendChart from './TrendChart';
import {
  normalizeType, hasVal, fmtWon, fmtAum, fmtPct, pctColor, disparity,
  RETURN_PERIODS, getTrendValues, hasTrend, glassCard,
} from './etfUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

function Shell({ children }) {
  return (
    <div className="min-h-screen text-white font-sans" style={{ background: '#060c1a' }}>
      <SiteHeader />
      <div className="relative max-w-4xl mx-auto px-4 md:px-8 pt-24 pb-16" style={{ zIndex: 10 }}>
        {children}
      </div>
      <SiteFooter />
    </div>
  );
}

// 상세 표의 한 행
function Row({ label, value, valueColor }) {
  return (
    <div className="flex items-center justify-between px-5 py-3.5 border-b border-white/8 last:border-b-0">
      <span className="text-sm text-white/50">{label}</span>
      <span className="text-sm font-semibold tabular-nums" style={valueColor ? { color: valueColor } : { color: '#fff' }}>
        {value}
      </span>
    </div>
  );
}

export default function EtfDetail() {
  const { id } = useParams();
  const [etf, setEtf] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/products`)
      .then((r) => { if (!r.ok) throw new Error('데이터를 불러오지 못했습니다.'); return r.json(); })
      .then((data) => {
        const found = (Array.isArray(data) ? data : []).find((p) => String(p.id) === String(id));
        if (!found) throw new Error('해당 ETF를 찾을 수 없습니다.');
        setEtf(found);
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return <Shell><p className="text-center text-white/50 py-20">불러오는 중...</p></Shell>;
  if (error || !etf) {
    return (
      <Shell>
        <div className="text-center py-20">
          <p className="text-white/60 mb-6">{error || 'ETF 정보가 없습니다.'}</p>
          <Link to="/" className="text-blue-400 hover:text-blue-300 text-sm">← 홈으로</Link>
        </div>
      </Shell>
    );
  }

  const r = etf.returns || {};
  const isUsa = normalizeType(etf.type) === 'usa';
  const disp = disparity(etf.price, etf.nav);
  const y1 = r['1y'];

  return (
    <Shell>
      <Link to="/" className="inline-flex items-center gap-2 text-white/40 hover:text-white/70 text-sm mb-6">
        <ArrowLeft className="w-4 h-4" /> 목록으로
      </Link>

      {/* 헤더 */}
      <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
        <div>
          <div className="flex items-center gap-3 flex-wrap mb-2">
            <h1 className="text-3xl font-black">{etf.name || '(이름 없음)'}</h1>
            {hasVal(etf.ticker) && <span className="text-white/50 font-mono text-base">{etf.ticker}</span>}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${
              isUsa ? 'bg-indigo-500/20 text-indigo-300' : 'bg-blue-500/20 text-blue-300'}`}>
              {isUsa ? '🇺🇸 미국형' : '🇰🇷 한국형'}
            </span>
            {etf.isMonthly && <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-green-500/20 text-green-300">월배당</span>}
          </div>
        </div>
        <Link to={`/compare?ids=${encodeURIComponent(etf.id)}`}
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-700 text-sm font-bold no-underline transition-colors">
          <BarChart3 className="w-4 h-4" /> 다른 ETF와 비교
        </Link>
      </div>

      {/* 현재가 강조 */}
      <div className="flex items-baseline gap-4 mb-6 flex-wrap">
        <span className="text-4xl font-black tabular-nums">{fmtWon(etf.price)}</span>
        {hasVal(y1) && (
          <span className="text-lg font-bold tabular-nums" style={{ color: pctColor(y1) }}>
            1년 {fmtPct(y1)}
          </span>
        )}
      </div>

      {/* 차트 */}
      <div className="rounded-3xl p-6 mb-6" style={glassCard}>
        <div className="flex items-center justify-between mb-1">
          <h2 className="text-lg font-bold">가격 추이</h2>
          <span className="text-[11px] text-white/35">기간 수익률 기반 추정치</span>
        </div>
        <p className="text-xs text-white/35 mb-4">
          공시된 기간 수익률(1개월·3개월·6개월·1년)로 역산한 추정 추이로, 실제 일별 시세와 다를 수 있습니다.
        </p>
        {hasTrend(etf) ? (
          <TrendChart series={[{ name: etf.name, color: '#60a5fa', values: getTrendValues(etf) }]}
            formatY={hasVal(etf.price) ? undefined : (v) => v.toFixed(0)} />
        ) : (
          <p className="text-center text-white/40 py-10 text-sm">추이를 그릴 수익률 데이터가 부족합니다.</p>
        )}
      </div>

      {/* 상세 정보 표 */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="rounded-2xl overflow-hidden" style={glassCard}>
          <h3 className="text-sm font-bold px-5 py-3 border-b border-white/10 text-white/70">시세 정보</h3>
          <Row label="현재가" value={fmtWon(etf.price)} />
          <Row label="기준가 (NAV)" value={fmtWon(etf.nav)} />
          <Row label="괴리율" value={fmtPct(disp)} valueColor={pctColor(disp)} />
          <Row label="순자산 (AUM)" value={fmtAum(etf.aum)} />
        </div>

        <div className="rounded-2xl overflow-hidden" style={glassCard}>
          <h3 className="text-sm font-bold px-5 py-3 border-b border-white/10 text-white/70">기간 수익률</h3>
          {RETURN_PERIODS.map(([k, label]) => (
            <Row key={k} label={label} value={fmtPct(r[k])} valueColor={pctColor(r[k])} />
          ))}
        </div>
      </div>

      {/* 기본 정보 + 태그 */}
      <div className="rounded-2xl overflow-hidden mt-6" style={glassCard}>
        <h3 className="text-sm font-bold px-5 py-3 border-b border-white/10 text-white/70">기본 정보</h3>
        <Row label="구분" value={isUsa ? '미국형 ETF' : '한국형 ETF'} />
        <Row label="종목코드/티커" value={hasVal(etf.ticker) ? etf.ticker : '—'} />
        <Row label="배당 주기" value={etf.isMonthly ? '월배당' : '—'} />
        {(etf.tags || []).length > 0 && (
          <div className="px-5 py-3.5">
            <span className="text-sm text-white/50 block mb-2">태그</span>
            <div className="flex flex-wrap gap-1.5">
              {etf.tags.map((t, i) => (
                <span key={i} className="px-2 py-0.5 rounded-md text-xs bg-white/10 text-white/60">{t}</span>
              ))}
            </div>
          </div>
        )}
      </div>
    </Shell>
  );
}
